import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../../services/api';
import { HelpCircle, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * AirTrafficPanel Component
 * Displays ADS-B Exchange map for Gulf airspace
 */
const AirTrafficPanel = () => {
  const [flightData, setFlightData] = useState(null);

  useEffect(() => {
    const loadFlightData = async () => {
      try {
        const res = await dashboardAPI.getFlightCounts();
        setFlightData(res.data);
      } catch (error) {
        console.error('Failed to load flight counts', error);
      }
    };

    loadFlightData();
    const interval = setInterval(loadFlightData, 60 * 60 * 1000); // Refresh hourly
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = () => {
    if (!flightData) return null;
    if (flightData.status === 'restricted') {
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    }
    return <CheckCircle className="w-5 h-5 text-green-500" />;
  };

  const getStatusTooltip = () => {
    if (!flightData) return 'Flight data loading...';
    if (flightData.status === 'restricted') {
      return 'Significant decrease in air traffic detected. This may indicate airspace restrictions or flight cancellations, which could lead to increased ticket prices and longer travel times.';
    }
    return 'Air traffic is operating normally. No significant disruptions detected.';
  };

  return (
    <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Air Traffic - Gulf Airspace
      </h2>

      {/* Status Indicator */}
      {flightData && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              {getStatusIcon()}
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Status: <span className={`font-semibold ${flightData.status === 'restricted' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {flightData.status === 'restricted' ? 'Restricted' : 'Normal'}
                </span>
              </span>
            </div>
            <div className="group relative">
              <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
              <div className="absolute right-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                {getStatusTooltip()}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total Flights: </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {flightData.current?.total || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Commercial: </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {flightData.current?.commercial || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Military: </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {flightData.current?.military || 0}
              </span>
            </div>
            <div className="col-span-3">
              <span className="text-gray-600 dark:text-gray-400">7-Day Average: </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {flightData.baseline7d?.avg || 0}
              </span>
              <span className="text-gray-600 dark:text-gray-400 ml-4">Deviation: </span>
              <span className={`font-semibold ${flightData.deviation < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {flightData.deviation ? `${flightData.deviation > 0 ? '+' : ''}${flightData.deviation.toFixed(1)}%` : '0%'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Aviation Maps - Both ADS-B Exchange and AirNavRadar */}
      <div className="space-y-6">
        {/* ADS-B Exchange Map */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            ADS-B Exchange - Global Flight Tracking
            <div className="group relative ml-2">
              <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
              <div className="absolute left-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                Real-time flight positions from ADS-B Exchange. Shows commercial and military aircraft globally. Commercial flights in blue, military in red.
              </div>
            </div>
          </div>
          <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <iframe
              src="https://globe.adsbexchange.com/?embed=true&lat=26.5&lon=56.5&zoom=8"
              title="ADS-B Exchange Flight Map"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Global flight tracking with detailed aircraft information
          </p>
        </div>

        {/* AirNavRadar Map */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            AirNavRadar - Middle East Aviation Map
            <div className="group relative ml-2">
              <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
              <div className="absolute left-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                Public aviation radar focused on Middle East region. Shows flight density and air traffic patterns. Reduced traffic may indicate airspace restrictions.
              </div>
            </div>
          </div>
          <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <iframe
              src="https://www.airnavradar.com/?widget=1&z=5&lat=32.4279&lng=53.6880"
              title="Public aviation map"
              className="aviation-map w-full h-full"
              sandbox="allow-scripts allow-popups allow-same-origin"
              loading="lazy"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Middle East focused aviation radar. Possible ticket price rise if restrictions continue.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AirTrafficPanel;

